/**
 * Typed event bus for decoupled cross-module communication.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { Alert } from '@/intelligence/alerts/types';
import type { Entity } from '@/intelligence/entities/types';
import type {
  Investigation,
  InvestigationState,
  CollectionTask,
  ResourceState,
  CoverageReport,
  DraftAssessment,
} from '@/intelligence/investigation/types';
import type {
  AnalysisResult,
  FocalPoint,
  SurgeEvent,
  ConvergenceCell,
  HotspotEscalation,
  CascadeResult,
  CompositeRisk,
  VelocityMetrics,
  TrendingKeyword,
  AggregatedSignal,
} from '@/intelligence/analysis/types';
import type { BreakingNewsAlert } from '@/intelligence/analysis/breaking-news';
import type { SentimentScore } from '@/intelligence/analysis/sentiment';
import type { AISAnomaly } from '@/intelligence/analysis/ais-spoofing';
import type { PopulationExposure } from '@/intelligence/analysis/population-exposure';

export interface EventMap {
  'event:added': NormalizedEvent;
  'event:selected': NormalizedEvent;
  'event:batch': NormalizedEvent[];
  'layer:toggled': { layerId: string; visible: boolean };
  'variant:changed': { from: string; to: string };
  'panel:toggled': { panelId: string; visible: boolean };
  'alert:triggered': Alert;
  'source:status': { sourceId: string; state: string };
  'map:click': { lat: number; lng: number };
  'map:viewChanged': { center: [number, number]; zoom: number };
  'country:hover': { name: string; code: string } | null;
  'country:selected': { name: string; code: string };
  'theme:changed': 'dark' | 'light';
  'pipeline:error': { stage: string; error: string };
  'ai:summary:complete': { eventId: string; summary: string; provider: string };
  'ai:classify:complete': { eventId: string; result: import('@/intelligence/ai/types').ClassifierResult };
  'ai:provider:status': { providerId: string; available: boolean };
  'update:available': { currentVersion: string; latestVersion: string; releaseUrl: string };
  'analysis:complete': AnalysisResult;
  'analysis:focal-point': FocalPoint;
  'focal-point:selected': FocalPoint;
  'analysis:surge': SurgeEvent;
  'analysis:convergence': ConvergenceCell;
  'analysis:hotspot': HotspotEscalation;
  'analysis:cascade': CascadeResult;
  'analysis:risk': CompositeRisk;
  'analysis:velocity': VelocityMetrics;
  'analysis:trending': TrendingKeyword[];
  'analysis:signal': AggregatedSignal;
  'signal:inspect': AggregatedSignal;
  'analysis:breaking': BreakingNewsAlert;
  'analysis:sentiment': SentimentScore[];
  'analysis:ais-anomaly': AISAnomaly;
  'analysis:population-exposure': { eventId: string; exposure: PopulationExposure };
  'country:dossier': { name: string; code: string };
  'tv:enter': null;
  'tv:exit': null;
  // Phase 2: Entity events
  'entity:created': Entity;
  'entity:updated': Entity;
  'entity:deleted': string;
  'entity:selected': Entity;
  // Phase 2: Investigation events
  'investigation:start': Investigation;
  'investigation:state': { investigationId: string; from: InvestigationState; to: InvestigationState };
  'investigation:complete': Investigation;
  'investigation:error': { investigationId: string; error: string; state: InvestigationState };
  'investigation:entity-extracted': { investigationId: string; entity: Entity };
  'investigation:task-completed': { investigationId: string; task: CollectionTask };
  'investigation:resource-update': { investigationId: string; resource: ResourceState };
  'investigation:coverage-update': { investigationId: string; coverage: CoverageReport };
  'investigation:synthesis-complete': { investigationId: string; assessment: DraftAssessment };
  // Phase 2: Investigation context signals
  'investigation:context-signal': { investigationId: string; signalType: string; summary: string; data: unknown };
  'investigation:lifecycle-changed': { investigationId: string; from: string; to: string };
  // Phase 2: Investigation UI triggers
  'investigation:request': { query: string; tier: import('@/intelligence/investigation/types').InvestigationTierName };
  'investigation:open-graph': Investigation;
}

type Handler<T> = (data: T) => void;

export interface EventBus {
  emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void;
  on<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): () => void;
  off<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): void;
}

export function createEventBus(): EventBus {
  const handlers = new Map<string, Set<Handler<unknown>>>();

  function emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    const set = handlers.get(event as string);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(data);
      } catch (err) {
        console.error(`[Sentinel] Error in handler for "${event as string}":`, err);
      }
    }
  }

  function on<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): () => void {
    const key = event as string;
    if (!handlers.has(key)) {
      handlers.set(key, new Set());
    }
    handlers.get(key)!.add(handler as Handler<unknown>);
    return () => off(event, handler);
  }

  function off<K extends keyof EventMap>(event: K, handler: Handler<EventMap[K]>): void {
    const set = handlers.get(event as string);
    if (set) {
      set.delete(handler as Handler<unknown>);
    }
  }

  return { emit, on, off };
}

/** Singleton event bus */
export const eventBus = createEventBus();
